import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {
  Input,
  Autocomplete,
  DatePicker,
} from 'components/FormInputs';
import countries from 'constants/countries';

const HostInformationFormNaturalSection = ({
  user,
  formik,
}) => {
  const intl = useIntl();

  const {
    errors, values, handleChange, setFieldValue, handleBlur,
  } = formik;
  const userVerified = !!user.bankid_verified_at || user.kyc_status === 'SUCCEDED' || user.kyc_status === 'PENDING';
  const dac7article = '168470';
  return (
    <>
      <Grid item xs={12} sm={6}>
        <Input
          name="tin_number"
          value={values.tin_number}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={values.nationality === 'SE'
            ? (
              <FormattedMessage
                id="Social security number"
                defaultMessage="Social security number"
              />
            )
            : (
              <FormattedMessage
                id="Taxpayer Identification Number"
                defaultMessage="Taxpayer Identification Number"
              />
            )}
          InputProps={{ inputProps: { maxLength: 80 } }}
          required
          disabled={!!user.tin_number && userVerified}
          help={values.nationality !== 'SE'
            ? (window.supportHeroWidget && (
              <>
                <button
                  className="a primary"
                  onClick={() => window.supportHeroWidget.show({ article: dac7article })}
                  type="button"
                >
                  <FormattedMessage
                    id="Click here"
                    defaultMessage="Click here"
                  />
                </button>{' '}
                <FormattedMessage
                  id="label.more.help"
                  defaultMessage="for more information"
                />
              </>
            )) : (
              <FormattedMessage
                id="label.pnr.format"
                defaultMessage="YYMMDDNNNN"
              />
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          name="birthday"
          value={values.birthday || null}
          onBlur={handleBlur}
          onChange={(date) => {
            if (date) {
              setFieldValue('birthday', date);
            }

            if (date === null) {
              setFieldValue('birthday', null);
            }
          }}
          label={(
            <FormattedMessage
              id="userform.birthday"
              defaultMessage="Birthday"
            />
          )}
          cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
          clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
          errors={errors}
          minDate="1917-01-01"
          maxDate="2017-01-01"
          openTo="year"
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="first_name"
          value={values.first_name}
          onChange={(e) => {
            setFieldValue('first_name', e.target.value);
            setFieldValue('owner', `${e.target.value} ${values.last_name}`);
          }}
          onBlur={handleBlur}
          errors={errors}
          label={(
            <FormattedMessage
              id="userform.firstName"
              defaultMessage="First name"
            />
          )}
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="last_name"
          value={values.last_name}
          onChange={(e) => {
            setFieldValue('last_name', e.target.value);
            setFieldValue('owner', `${values.first_name} ${e.target.value}`);
          }}
          onBlur={handleBlur}
          errors={errors}
          label={(
            <FormattedMessage
              id="userform.lastName"
              defaultMessage="Last name"
            />
          )}
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="country_of_residence"
          label={(
            <FormattedMessage
              id="userform.country"
              defaultMessage="Country of residence"
            />
          )}
          errors={errors}
          value={values.country_of_residence}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('country_of_residence', selected.code);
          }}
          options={countries[intl.locale]}
          required
          disabled={userVerified}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="nationality"
          label={(
            <FormattedMessage
              id="userform.nationality"
              defaultMessage="Nationality"
            />
          )}
          errors={errors}
          value={values.nationality}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('nationality', selected.code);
          }}
          options={countries[intl.locale]}
          required
          disabled={userVerified}
        />
      </Grid>
      <Box mt={2}>&nbsp;</Box>
      <Grid item xs={12}>
        <Typography variant="h6">
          <FormattedMessage
            id="Address"
            defaultMessage="Address"
          />
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_address1"
          value={values.u_address1}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={(
            <FormattedMessage
              id="userform.address1"
              defaultMessage="Street address"
            />
          )}
          InputProps={{ inputProps: { maxLength: 40 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_address2"
          value={values.u_address2}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={(
            <FormattedMessage
              id="userform.address2"
              defaultMessage="Address line 2"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_postal_code"
          value={values.u_postal_code}
          onChange={!values.u_country || values.u_country === 'SE' ? ((e) => {
            e.target.value = (e.target.value || '').replace(/[^0-9]/g, '');
            return handleChange(e);
          }) : handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={(
            <FormattedMessage
              id="userform.postalCode"
              defaultMessage="Postal code"
            />
          )}
          InputProps={{ inputProps: { maxLength: 8 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          name="u_city"
          value={values.u_city}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          label={(
            <FormattedMessage
              id="userform.city"
              defaultMessage="City"
            />
          )}
          InputProps={{ inputProps: { maxLength: 40 } }}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          onBlur={handleBlur}
          name="u_country"
          label={(
            <FormattedMessage
              id="userform.country"
              defaultMessage="Country of residence"
            />
          )}
          errors={errors}
          value={values.u_country}
          settings={{
            disableClearable: true,
            valueProp: 'code',
            labelProp: 'name',
          }}
          onChange={(selected) => {
            setFieldValue('u_country', selected.code);
          }}
          options={countries[intl.locale]}
          required
        />
      </Grid>
      {
        ['CA', 'US', 'MX'].includes(values.u_country) && (
          <Grid item xs={12} sm={6}>
            <Input
              name="u_region"
              value={values.u_region}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              label={(
                <FormattedMessage
                  id="userform.region"
                  defaultMessage="Region"
                />
              )}
              InputProps={{ inputProps: { maxLength: 40 } }}
              required
            />
          </Grid>
        )
      }
    </>
  );
};

HostInformationFormNaturalSection.propTypes = {
  user: PropTypes.shape({
    tin_number: PropTypes.string,
    kyc_status: PropTypes.string,
    bankid_verified_at: PropTypes.string,
  }),
  formik: PropTypes.shape({
    errors: PropTypes.shape({}),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
    values: PropTypes.shape({
      tin_number: PropTypes.string,
      birthday: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      country_of_residence: PropTypes.string,
      nationality: PropTypes.string,
      u_address1: PropTypes.string,
      u_address2: PropTypes.string,
      u_city: PropTypes.string,
      u_postal_code: PropTypes.string,
      u_country: PropTypes.string,
      u_region: PropTypes.string,
    }),
  }),
};

export default HostInformationFormNaturalSection;
